import type { ComponentPropsWithoutRef, ReactNode } from 'react';

import type { SwitchProps } from '@headlessui/react';
import { Switch } from '@headlessui/react';
import clsx from 'clsx';

type IconElementType = ({ enabled }: { enabled: boolean }) => ReactNode;

export type SwitchWithIconsProps = SwitchProps<'div'> & {
    enabled: boolean;
    setEnabled: (enabled: boolean) => void;
    srLabel?: string;
    size?: keyof typeof sizes;
    leftIcon: IconElementType;
    rightIcon: IconElementType;
    dotClassName: string;
};

type IconWrapperProps = ComponentPropsWithoutRef<'span'>;

const sizes = {
    default: {
        wrapper: 'w-11 h-5 rounded-full border-2',
        dot: 'h-4 w-5',
        leftIcon: 'left-1.25',
        rightIcon: 'right-1.25'
    },
    big: {
        wrapper: 'w-[62px] h-[26px] rounded-full border-3',
        dot: 'h-5 w-7',
        leftIcon: 'left-[7px]',
        rightIcon: 'right-[7px]'
    }
};

function transition(size: keyof typeof sizes, enabled: boolean) {
    switch (size) {
        case 'default':
            return clsx({ 'translate-x-5': enabled, 'translate-x-0': !enabled });
        case 'big':
            return clsx({
                'translate-x-7': enabled,
                'translate-x-0': !enabled
            });
    }
}

function IconWrapper({ children, className }: IconWrapperProps) {
    return (
        <span className={clsx('absolute z-1 flex h-full items-center', className)}>{children}</span>
    );
}
export function SwitchWithIcons({
    enabled,
    setEnabled,
    size = 'default',
    srLabel = 'toggle',
    leftIcon,
    rightIcon,
    className,
    dotClassName,
    ...restProps
}: SwitchWithIconsProps) {
    return (
        <Switch
            checked={enabled}
            onChange={setEnabled}
            className={clsx(
                'relative inline-flex shrink-0 cursor-pointer border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-0 focus:ring-offset-0',
                sizes[size].wrapper,
                className
            )}
            {...restProps}>
            <span className="sr-only">{srLabel}</span>
            <span
                aria-hidden="true"
                className={clsx(
                    'pointer-events-none inline-block transform rounded-full ring-0 transition duration-200 ease-in-out',
                    sizes[size].dot,
                    transition(size, enabled),
                    dotClassName
                )}
            />
            <IconWrapper className={sizes[size].leftIcon}>{leftIcon({ enabled })}</IconWrapper>
            <IconWrapper className={sizes[size].rightIcon}>{rightIcon({ enabled })}</IconWrapper>
        </Switch>
    );
}
